import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
    patchUserInstrument, createUserInstrument, getInstruments,
} from '../../../../features/sessions/sessionProfileNavigation';
import { Instrument, UserInstrument } from '../../../../#interfaces/interfaces';
import {
    Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface AddRemoveUserInstrumentsProps {
    userNickname: string;
    onClose: (v: boolean) => void;
}

export default function AddRemoveUserInstruments({ userNickname, onClose }: AddRemoveUserInstrumentsProps) {
    const instrumentsAvailable = useSelector((state: RootState) => state.sessionProfile.instrumentsAvailable);
    const userInstruments      = useSelector((state: RootState) => state.sessionProfile.userInstruments);
    const currentUser          = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders          = useSelector((state: RootState) => state.session.authHeaders);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (authHeaders) {
            dispatch(getInstruments());
        }
    }, []);

    async function handleSelect(instrumentId: number) {
        if (!authHeaders || !userNickname || !currentUser?.nickname || !currentUser?.id) return;

        const existing = userInstruments.find((u: UserInstrument) => u.instrument.id === instrumentId);

        if (existing?.selected === false) {
            await dispatch(patchUserInstrument({
                headers: authHeaders,
                urlParams: { userNickname },
                requestBodyData: { instrumentId, selected: true },
            }));
        } else if (existing?.id === undefined || !existing?.selected) {
            await dispatch(createUserInstrument({
                headers: authHeaders,
                urlParams: { currentUserNickname: userNickname },
                requestBodyData: { userId: currentUser.id, instrumentId },
            }));
        }
    }

    async function handleDeselect(instrumentId: number) {
        if (!authHeaders || !userNickname || !currentUser?.nickname || !currentUser?.id) return;

        const existing = userInstruments.find((u: UserInstrument) => u.instrument.id === instrumentId);
        if (existing?.selected === true) {
            await dispatch(patchUserInstrument({
                headers: authHeaders,
                urlParams: { userNickname },
                requestBodyData: { instrumentId, selected: false },
            }));
        }
    }

    return (
        <Dialog
            open
            onClose={() => onClose(false)}
            fullWidth
            maxWidth='xs'
            PaperProps={{
                sx: {
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '12px',
                    color: TEXT_PRIMARY,
                },
            }}
        >
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pb: 1, borderBottom: BORDER_SUBTLE,
            }}>
                <Box>
                    <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>Instrument Preferences</Typography>
                    <Box sx={{ width: 32, height: 3, bgcolor: BRAND_RED, mt: 0.5 }} />
                </Box>
                <IconButton onClick={() => onClose(false)} size='small' sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {instrumentsAvailable.map((instrument: Instrument, i: number) => {
                        const isSelected = userInstruments.some(
                            (u: UserInstrument) => u.instrument.name === instrument.name && u.selected === true,
                        );
                        return (
                            <Chip
                                key={i}
                                label={instrument.name}
                                size='small'
                                icon={isSelected
                                    ? <CheckIcon sx={{ fontSize: 14, color: `${TEXT_PRIMARY} !important` }} />
                                    : <AddIcon sx={{ fontSize: 14, color: `${TEXT_SECONDARY} !important` }} />}
                                onClick={() => isSelected ? handleDeselect(instrument.id) : handleSelect(instrument.id)}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    ...(isSelected ? {
                                        bgcolor: BRAND_RED,
                                        color: TEXT_PRIMARY,
                                        border: `1px solid ${BRAND_RED}`,
                                        '&:hover': { bgcolor: '#a93226' },
                                    } : {
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                        color: TEXT_SECONDARY,
                                        border: BORDER_SUBTLE,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: TEXT_PRIMARY },
                                    }),
                                }}
                            />
                        );
                    })}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
